import { useCallback } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import type { Href } from 'expo-router';
import { Link, useRouter } from 'expo-router';
import { LabelText, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ExpoRouterDemoBackButton = (params: Props) => {
  const {
    href,
    label = 'Go back',
    dismiss = false,
    color = theme.colors.WHITE,
    backgroundColor = theme.colors.KALE,
  } = params;

  const router = useRouter();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const pressableDynamicStyles = useStyle(
    () => ({ backgroundColor }),
    [backgroundColor],
  );

  const textDynamicStyles = useStyle(() => ({ color }), [color]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const goBack = useCallback(() => {
    if (!href) {
      router.back();

      return;
    }

    router.dismissTo(href);
  }, [href, router]);

  // ───────────────────────────────────────────────────────────────────────────

  if (!href) {
    return (
      <TouchableOpacity
        style={[styles.cta, pressableDynamicStyles]}
        onPress={goBack}
      >
        <LabelText sizeMatch={['12']} style={textDynamicStyles}>
          {label}
        </LabelText>
      </TouchableOpacity>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Link
      href={href}
      dismissTo={dismiss}
      style={[styles.cta, pressableDynamicStyles]}
    >
      <LabelText sizeMatch={['12']} style={textDynamicStyles}>
        {label}
      </LabelText>
    </Link>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cta: {
    borderRadius: theme.radius.large,
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['3'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  href?: Href;
  label?: string;
  dismiss?: boolean;
  color?: string;
  backgroundColor?: string;
};
